import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { GridBackground } from '@/components/GridBackground';
import {
    Wrench,
    ArrowRightLeft,
    CheckCircle,
    Wifi,
    FileCode,
    ChevronRight,
    Shield,
    HelpCircle,
    Globe,
    Bookmark,
    Star
} from 'lucide-react';

// Tool Components
import { BandwidthCalculator } from '@/components/tools/BandwidthCalculator';
import { M3UToXCConverter } from '@/components/tools/M3UToXCConverter';
import { XCToM3UConverter } from '@/components/tools/XCToM3UConverter';
import { M3UChecker } from '@/components/tools/M3UChecker';
import { MyIPChecker } from '@/components/tools/MyIPChecker';

export const ToolsPage = () => {
    const { toolId } = useParams();
    const [bookmarked, setBookmarked] = useState(false);

    const TOOLS = [
        {
            id: 'm3u-to-xtream',
            label: 'M3U to Xtream',
            desc: 'Extract credentials from M3U',
            icon: ArrowRightLeft,
            component: M3UToXCConverter,
            seoTitle: 'Convert to M3U URL Online - Extract Xtream Codes | XtreamGate',
            seoDesc: 'Easily convert to M3U URL online and extract Xtream Codes credentials. Our free tool parses username, password, and server URL from any playlist link.'
        },
        {
            id: 'xtream-to-m3u',
            label: 'Xtream to M3U',
            desc: 'Generate M3U from login',
            icon: FileCode,
            component: XCToM3UConverter,
            seoTitle: 'Xtream Codes to M3U Converter - Generate Playlist',
            seoDesc: 'Generate a valid M3U playlist URL from your Xtream Codes login details. Compatible with all IPTV players.'
        },
        {
            id: 'checker',
            label: 'M3U Subscription Checker',
            desc: 'Test subscription status',
            icon: Shield,
            component: M3UChecker,
            seoTitle: 'M3U Subscription Checker - Verify IPTV & Xtream Codes',
            seoDesc: 'Check if your IPTV subscription is active. Verify M3U URLs and Xtream Codes accounts instantly with our free client-side tester.'
        },
        {
            id: 'my-ip',
            label: 'My IP Info',
            desc: 'Check IP & Privacy',
            icon: Globe,
            component: MyIPChecker,
            seoTitle: 'What is My IP? - Check ISP & VPN Status | XtreamGate',
            seoDesc: 'Check your public IP address, ISP, and location. Verify if your VPN is working correctly to protect your streaming privacy.'
        },
        {
            id: 'bandwidth',
            label: 'Bandwidth Calc',
            desc: 'Estimate streaming usage',
            icon: Wifi,
            component: BandwidthCalculator,
            seoTitle: 'IPTV Bandwidth Calculator - Data Usage Estimator',
            seoDesc: 'Calculate Internet data usage for streaming IPTV in SD, HD, and 4K. Optimize your connection settings.'
        }
    ];

    const activeToolData = toolId ? TOOLS.find(t => t.id === toolId) : null;
    const ActiveComponent = activeToolData?.component;

    const [showBookmarkHint, setShowBookmarkHint] = useState(false);

    // Handle Bookmark
    const handleBookmark = () => {
        setShowBookmarkHint(true);
        // Auto-hide after 3 seconds
        setTimeout(() => setShowBookmarkHint(false), 3000);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [toolId]);

    // RENDER: HUB VIEW (No Tool ID)
    if (!toolId || !activeToolData) {
        return (
            <>
                <Helmet>
                    <title>Free IPTV Tools - Converters, Checkers & Utilities | XtreamGate</title>
                    <meta name="description" content="Access free professional IPTV tools: M3U to Xtream Converter, Subscription Checker, IP Info, and Bandwidth Calculator. 100% Client-Side & Secure." />
                    <link rel="canonical" href="https://xtreamgate.com/tools" />
                </Helmet>

                <MainNavigation />

                <div className="min-h-screen relative overflow-hidden">
                    <GridBackground />
                    <div className="relative z-10 pt-32 pb-24 px-4">
                        <div className="max-w-7xl mx-auto text-center mb-16">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6 backdrop-blur-md shadow-lg">
                                    <Wrench className="w-4 h-4 text-primary" />
                                    <span className="text-sm font-bold text-primary uppercase tracking-wider">Professional Utilities</span>
                                </div>
                                <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                                    Free IPTV <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">Toolkit</span>
                                </h1>
                                <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
                                    Essential utilities for IPTV users. Convert formats, verify subscriptions, and check your security—all running securely in your browser.
                                </p>
                                <button
                                    onClick={handleBookmark}
                                    className="relative inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-full transition-all text-white font-medium group"
                                >
                                    {bookmarked ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Bookmark className="w-5 h-5 text-primary group-hover:text-white" />}
                                    {bookmarked ? 'Saved to Bookmarks' : 'Bookmark Page'}

                                    {/* Smart Hint Toast */}
                                    <AnimatePresence>
                                        {showBookmarkHint && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                                                className="absolute top-full mt-4 left-1/2 -translate-x-1/2 w-64 p-4 bg-gray-900/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-50 text-center"
                                            >
                                                <div className="text-sm font-bold text-white mb-1">
                                                    {navigator.platform.indexOf('Mac') > -1 ? 'Press ⌘ + D' : 'Press Ctrl + D'}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    to save this page to your browser.
                                                </div>
                                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-gray-900/90 border-t border-l border-white/10 transform rotate-45"></div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </button>
                            </motion.div>
                        </div>

                        {/* Tools Grid - Hub View */}
                        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
                            {TOOLS.map((tool, index) => (
                                <Link to={`/tools/${tool.id}`} key={tool.id}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="h-full glass p-8 rounded-3xl border border-white/10 hover:border-primary/50 transition-all group relative overflow-hidden"
                                    >
                                        <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ChevronRight className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                            <tool.icon className="w-7 h-7 text-primary" />
                                        </div>
                                        <h3 className="text-2xl font-bold mb-3 text-white">{tool.label}</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            {tool.desc}.
                                        </p>
                                    </motion.div>
                                </Link>
                            ))}
                        </div>

                        {/* AIO / SEO Content Section */}
                        <div className="max-w-4xl mx-auto space-y-16">
                            <div className="text-center">
                                <h2 className="text-3xl font-bold mb-6">Why use XtreamGate Tools?</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                                    <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                                        <Shield className="w-8 h-8 text-green-400 mb-4" />
                                        <h4 className="font-bold text-lg mb-2">100% Client-Side</h4>
                                        <p className="text-sm text-gray-400">Your playlists and credentials never leave your device. All processing happens in your browser.</p>
                                    </div>
                                    <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                                        <Star className="w-8 h-8 text-yellow-400 mb-4" />
                                        <h4 className="font-bold text-lg mb-2">Always Free</h4>
                                        <p className="text-sm text-gray-400">No subscriptions, no hidden fees. Just powerful utilities for the community.</p>
                                    </div>
                                    <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                                        <Globe className="w-8 h-8 text-blue-400 mb-4" />
                                        <h4 className="font-bold text-lg mb-2">Universal Compatibility</h4>
                                        <p className="text-sm text-gray-400">Works with all IPTV players: TiviMate, Smarters, XCIPTV, and more.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Sports Hub Promo */}
                            <Link
                                to="/sports"
                                className="group block relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-r from-red-500/10 to-transparent p-6 hover:border-red-500/50 transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center relative">
                                            <span className="text-2xl">⚽</span>
                                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-white group-hover:text-red-400 transition">Live Sports Hub</h3>
                                            <p className="text-sm text-muted-foreground">Premier League, NBA, NFL scores • Streaming guides</p>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-6 h-6 text-red-400 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>

                            {/* FAQ Section (Reused from previous) */}
                            <div className="space-y-6">
                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
                                </div>
                                <div className="glass p-6 rounded-2xl border border-white/10">
                                    <h3 className="flex items-center gap-3 text-lg font-bold mb-3 text-white">
                                        <HelpCircle className="w-5 h-5 text-primary" />
                                        Is it safe to convert M3U online?
                                    </h3>
                                    <p className="text-gray-400 leading-relaxed">
                                        Yes, if you use a client-side tool like ours. Unlike other sites that process your data on their servers, XtreamGate processes everything locally on your computer.
                                    </p>
                                </div>
                                <div className="glass p-6 rounded-2xl border border-white/10">
                                    <h3 className="flex items-center gap-3 text-lg font-bold mb-3 text-white">
                                        <HelpCircle className="w-5 h-5 text-primary" />
                                        Why do I need to convert M3U to Xtream?
                                    </h3>
                                    <p className="text-gray-400 leading-relaxed">
                                        Many modern players (like TiviMate) work better with Xtream Codes (Username/Password) than long M3U URLs. It allows for better EPG loading and catch-up features.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </>
        );
    }

    // RENDER: ACTIVE TOOL VIEW (Specific Tool ID)
    return (
        <>
            <Helmet>
                <title>{activeToolData.seoTitle} | XtreamGate</title>
                <meta name="description" content={activeToolData.seoDesc} />
                <link rel="canonical" href={`https://xtreamgate.com/tools/${activeToolData.id}`} />
                {/* JSON-LD Schema for SoftwareApplication */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": activeToolData.label,
                        "applicationCategory": "UtilitiesApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "USD"
                        },
                        "description": activeToolData.desc,
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.9",
                            "ratingCount": "1205"
                        }
                    })}
                </script>
            </Helmet>

            <MainNavigation />

            <div className="min-h-screen relative overflow-hidden">
                <GridBackground />

                {/* Content Wrapper */}
                <div className="relative z-10">
                    {/* Header Section */}
                    <section className="pt-32 pb-12 px-4 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            key={activeToolData.id} // Re-animate on tool change
                        >
                            <Link to="/tools" className="inline-flex items-center gap-2 mb-6 text-sm text-muted-foreground hover:text-white transition-colors">
                                <ArrowRightLeft className="w-3 h-3 rotate-180" /> Back to Tools Hub
                            </Link>
                            <div className="flex justify-center mb-6">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-md shadow-lg">
                                    <Wrench className="w-4 h-4 text-primary" />
                                    <span className="text-sm font-bold text-primary uppercase tracking-wider">Free Utility</span>
                                </div>
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                                {activeToolData.label}
                            </h1>
                            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                {activeToolData.desc}. Professional-grade utility, 100% Free & Secure.
                            </p>
                        </motion.div>
                    </section>

                    {/* Dashboard Layout */}
                    <div className="max-w-7xl mx-auto px-4 pb-24">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                            {/* Sidebar Navigation */}
                            <div className="lg:col-span-3 space-y-4">
                                <div
                                    className="rounded-2xl p-4 border border-white/10 sticky top-24"
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        backdropFilter: 'blur(20px) saturate(180%)',
                                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
                                    }}
                                >
                                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 px-2">Navigate Tools</h3>
                                    <div className="space-y-2">
                                        {TOOLS.map((tool) => (
                                            <Link
                                                key={tool.id}
                                                to={`/tools/${tool.id}`}
                                                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all group ${activeToolData.id === tool.id
                                                    ? 'bg-primary/20 border border-primary/30 shadow-[0_0_15px_rgba(var(--primary-rgb),0.2)] text-white'
                                                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${activeToolData.id === tool.id ? 'bg-primary text-white' : 'bg-white/5 text-gray-400 group-hover:text-white'}`}>
                                                        <tool.icon className="w-4 h-4" />
                                                    </div>
                                                    <div className="text-left">
                                                        <div className="font-semibold text-sm">{tool.label}</div>
                                                    </div>
                                                </div>
                                                {activeToolData.id === tool.id && <ChevronRight className="w-4 h-4 text-primary" />}
                                            </Link>
                                        ))}
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                        <Link to="/tools" className="block text-center text-xs font-medium text-muted-foreground hover:text-white py-2">
                                            View All Tools
                                        </Link>
                                    </div>
                                </div>

                                {/* Trust Badge */}
                                <div
                                    className="rounded-2xl p-6 border border-white/10 text-center"
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        backdropFilter: 'blur(20px)'
                                    }}
                                >
                                    <div className="inline-flex items-center justify-center p-3 bg-green-500/10 rounded-full mb-3">
                                        <CheckCircle className="w-6 h-6 text-green-400" />
                                    </div>
                                    <h4 className="font-bold text-white mb-1">Client-Side Secure</h4>
                                    <p className="text-xs text-muted-foreground">All processing happens in your browser. No data is sent to our servers.</p>
                                </div>
                            </div>

                            {/* Active Tool Content */}
                            <div className="lg:col-span-9">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeToolData.id}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <ActiveComponent />
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};
